import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { submitLead } from "../../lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, CheckCircle, ShieldCheck } from "@phosphor-icons/react";

export const DemoModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    gpu_count: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const close = (val) => {
    if (!val) setTimeout(() => { setDone(false); setLoading(false); }, 300);
    onOpenChange(val);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast.error("Заполните обязательные поля", { description: "Имя, компания, email" });
      return;
    }
    setLoading(true);
    try {
      await submitLead({ ...form, source: "demo_form" });
      setDone(true);
      toast.success("Заявка отправлена", { description: "Свяжемся в течение 24 часов" });
    } catch (err) {
      toast.error("Не удалось отправить", { description: "Попробуйте ещё раз или напишите на email" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        data-testid="demo-modal"
        className="bg-ink-surface border border-ink-text/10 text-ink-text max-w-xl p-0 overflow-hidden"
      >
        <div className="p-7 sm:p-9">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold tracking-tight text-2xl">
              Запросить демо InBoost
            </DialogTitle>
            <DialogDescription className="text-muted mono text-xs uppercase tracking-widest mt-1">
              30-минутный технический брифинг
            </DialogDescription>
          </DialogHeader>

          {done ? (
            <div className="mt-8 text-center py-8" data-testid="demo-success">
              <div className="inline-flex w-14 h-14 rounded-full bg-success/10 border border-success/40 items-center justify-center mb-5">
                <CheckCircle size={28} weight="duotone" className="text-success" />
              </div>
              <div className="font-heading text-2xl font-bold mb-2">Спасибо!</div>
              <p className="text-sm text-muted max-w-sm mx-auto leading-relaxed">
                Мы получили заявку и пришлём приглашение на встречу в ближайшее время.
              </p>
              <button
                onClick={() => close(false)}
                className="mt-7 btn-secondary rounded-lg px-5 py-2.5 font-semibold"
              >
                Закрыть
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-7 space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Имя *" value={form.name} onChange={(v) => set("name", v)} testid="demo-name" />
                <Field label="Компания *" value={form.company} onChange={(v) => set("company", v)} testid="demo-company" />
                <Field label="Email *" type="email" value={form.email} onChange={(v) => set("email", v)} testid="demo-email" />
                <Field label="Телефон" value={form.phone} onChange={(v) => set("phone", v)} testid="demo-phone" />
                <div className="sm:col-span-2">
                  <Field label="Количество GPU" value={form.gpu_count} onChange={(v) => set("gpu_count", v)} testid="demo-gpu" placeholder="напр. 256" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block">
                    <div className="mono text-[10px] uppercase tracking-widest text-muted mb-1.5">
                      Сообщение
                    </div>
                    <textarea
                      rows={3}
                      value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                      data-testid="demo-message"
                      placeholder="Опишите задачу или нагрузку — H100, RTX 5090, тип воркфлоу…"
                      className="w-full rounded-lg bg-ink border border-ink-text/10 px-3.5 py-2.5 text-sm outline-none focus:border-brand-cyan transition placeholder:text-muted-deep resize-none"
                    />
                  </label>
                </div>
              </div>

              <div className="flex items-start gap-2 text-[11px] text-muted leading-relaxed pt-2" data-testid="demo-legal-notice">
                <ShieldCheck size={12} weight="fill" className="text-brand-cyan mt-0.5 flex-shrink-0" />
                <div>
                  Форма «Запросить демо» использует технологии электронной почты (email) для
                  передачи указанных вами сведений. Заполнение и отправка формы являются
                  добровольным волеизъявлением; передача персональных данных осуществляется
                  на ваше усмотрение. Отправляя форму, вы подтверждаете согласие на обработку
                  введённых данных в целях обратной связи и подготовки коммерческого
                  предложения в соответствии с Федеральным законом № 152-ФЗ «О персональных
                  данных». Данные не передаются третьим лицам.
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => close(false)}
                  className="rounded-lg px-5 py-2.5 text-sm text-muted hover:text-ink-text"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="demo-submit"
                  className="btn-primary rounded-lg px-5 py-2.5 font-semibold inline-flex items-center gap-2 disabled:opacity-60"
                >
                  <PaperPlaneTilt size={16} weight="fill" />
                  {loading ? "Отправляем…" : "Отправить заявку"}
                </button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, value, onChange, type = "text", testid, placeholder }) => (
  <label className="block">
    <div className="mono text-[10px] uppercase tracking-widest text-muted mb-1.5">{label}</div>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full rounded-lg bg-ink border border-ink-text/10 px-3.5 py-2.5 text-sm text-ink-text outline-none focus:border-brand-cyan transition placeholder:text-muted-deep"
    />
  </label>
);

export default DemoModal;
